import { FCC } from "@/types/common";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { CompanyBasicInfo } from "@/features/company/components/company-info/CompanyBasicInfo";
import { CompanyIndustryFieldInfo } from "@/features/company/components/company-info/CompanyIndustryFieldInfo";
import { useForm } from "react-hook-form";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Stack, styled, Typography } from "@mui/material";
import { Form } from "@/components/elements/Form";
import { useDialog } from "@/hooks/useDialog";
import { useGetCompanySchema } from "@/features/company/hooks/useGetCompanySchema";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { createCompanyApi, getCompanyExists, updateCompanyApi } from "@/api/services/main/company";
import { showError } from "@/utils/error";
import { CompanyTitle } from "@/features/company/components/CompanyTitle";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { BusinessOfficeInfo } from "@/features/company/components/company-info/BusinessOfficeInfo";
import { CompanyBranchInfo } from "@/features/company/components/company-info/CompanyBranchInfo";
import { ContactInfo } from "@/features/company/components/company-info/ContactInfo";
import { CompanyAdditionalInfo } from "@/features/company/components/company-info/CompanyAdditionalInfo";
import { ContractInfo } from "@/features/company/components/company-info/ContractInfo";
import { Button } from "@/components/elements/Button";
import { CompanyFooterContainer } from "@/features/company/components/CompanyFooterContainer";
import { IconSave } from "@/assets/icons";
import { CompanyWarningListModal } from "@/features/company/components/CompanyWarningListModal";
import { TGetCompanyExistsDto } from "@/api/services/main/company/dtos/get-company.dto";
import { convertCompanyFormDataReq } from "@/features/company/utiles/convertCompanyFormData";
import { TCreateCompanyResDto } from "@/api/services/main/company/dtos/create-company-req.dto";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { CompanyDuplicationErrorModal } from "@/features/company/components/CompanyDuplicationErrorModal";

type TCompanyFormProps = {
  defaultValues?: Partial<TCompanyForm>;
  readonly?: boolean;
};
export const CompanyForm: FCC<TCompanyFormProps> = ({ defaultValues, readonly }) => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const confirmSave = useDialog();
  const { companySchema } = useGetCompanySchema();
  const methods = useForm<TCompanyForm>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: yupResolver(companySchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { handleSubmit, watch, getValues, setValue } = methods;
  const [confirmMessage] = useState<string>();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [checkCompanyType, setCheckCompanyType] = useState("");
  const [companyExist, setCompanyExist] = useState<TGetCompanyExistsDto[]>([]);
  const companyName = watch("companyName") ?? null;
  const representativeName = watch("representativeName") ?? null;
  const address = watch("address") ?? null;
  const duplicateModal = useDialog();
  const duplicateErrorModal = useDialog();
  const [isChangeValue, setIsChangeValue] = useState(false);
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<{ route: string } | null>(null);

  const watchValue = methods.watch();

  useEffect(() => {
    const hasValue = Object.values(watchValue).some((value) => {
      if (value === null || value === "" || value === undefined) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });
    setIsChangeValue(hasValue);
  }, [watchValue]);

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect.route);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  const onSubmit = async () => {
    await handleGetCompanyExist();
  };

  const handleDuplicateClose = () => duplicateModal.cancel();

  const handleGetCompanyExist = async () => {
    try {
      const { data: companyExists } = await getCompanyExists("create", companyName, representativeName, address, companyId);
      if (companyExists.type) {
        setCheckCompanyType(companyExists.type);
        setCompanyExist(companyExists.companies);
        if (companyExists.type === "exist") {
          await duplicateModal.show();
        } else {
          await duplicateErrorModal.show();
        }
      } else {
        await handleSaveData();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveData = async () => {
    setIsDisableButton(true);
    try {
      const payload = convertCompanyFormDataReq(getValues());
      let rs: TCreateCompanyResDto;
      let route: string;
      if (companyId) {
        rs = await updateCompanyApi(companyId, payload);
        route = getDynamicRoute(AppRoutes.companyDetail, { companyId: companyId });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, companyId] });
      } else {
        rs = await createCompanyApi(payload);
        route = AppRoutes.companyList;
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST] });
      }
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setIsDisableButton(false);
      setBlockerEnabled(false);
      setRedirect({ route });
    } catch (e) {
      setIsDisableButton(false);
      showError(e);
    }
  };

  return (
    <>
      <CompanyTitle title={defaultValues?.companyName} managementNumber={defaultValues?.managementNumber} isArchive={defaultValues?.isArchive} />

      <Stack mt={3}>
        <Form methods={methods} onSubmit={onSubmit} schema={companySchema} enableBlocker={blockerEnabled && isChangeValue}>
          <MenuNavigate
            contents={[
              { title: "企業基本情報", content: <CompanyBasicInfo readonly={readonly} /> },
              { title: "業種・分野", content: <CompanyIndustryFieldInfo readonly={readonly} /> },
              { title: "営業所情報", content: <BusinessOfficeInfo /> },
              { title: "事業所情報", content: <CompanyBranchInfo /> },
              { title: "担当者情報", content: <ContactInfo /> },
              { title: "その他の情報", content: <CompanyAdditionalInfo /> },
              { title: "基本契約書", content: <ContractInfo /> },
            ]}
            contentWidth="900px"
            contentAffix={
              <CompanyFooterContainer>
                <Stack direction="row" gap={3}>
                  <ButtonSave disabled={isDisableButton} type="submit" variant="primary" size="sm" startIcon={<IconSave />}>
                    保存
                  </ButtonSave>
                </Stack>
              </CompanyFooterContainer>
            }
          />
        </Form>
      </Stack>

      <ConfirmSaveModal open={confirmSave.open} onOk={confirmSave.confirm} onClose={confirmSave.cancel}>
        <Typography component="p" variant="subtitle1">
          {confirmMessage}
        </Typography>
      </ConfirmSaveModal>
      <CompanyWarningListModal
        label="同名の企業が登録されています。確認してください。"
        mode="duplicate-company"
        open={duplicateModal.open}
        onClose={handleDuplicateClose}
        onSave={handleSaveData}
        list={companyExist}
      />
      <CompanyDuplicationErrorModal
        message="同じ企業名かつ代表者名、所在地が一致する企業が既に登録されています。ご確認ください。"
        open={duplicateErrorModal.open}
        onClose={duplicateErrorModal.cancel}
        list={companyExist}
      />
    </>
  );
};

const ButtonSave = styled(Button)`
  width: 88px;
`;
